import { assign, fromCallback, setup } from 'xstate';
import type { AnyActorRef } from 'xstate';
import { DateTime } from 'luxon';

import reservationUtils from '@/services/reservationUtils';

import type { TripArrayFieldContext } from './createTripArrayFieldMachine';

export type AppointmentTimeAllowedForPortalInput = {
  appointmentDateTimeRef: AnyActorRef;
};

export type AppointmentTimeAllowedForPortalContext = {
  appointmentDateTimeRef: AnyActorRef;
  /** `null` disables the check (non-member portal or unknown portal). */
  portalName: string | null;
  isAllowedByIndex: boolean[];
};

export type AppointmentTimeAllowedForPortalEvent =
  | { type: 'APPOINTMENT_DATETIMES_UPDATED' }
  | { type: 'SET_PORTAL_NAME'; portalName: string | null }
  | { type: 'RESET' };

function readAppointmentDateTimes(ref: AnyActorRef): unknown[] {
  const ctx = ref.getSnapshot().context as TripArrayFieldContext;
  return ctx.values.length ? ctx.values : [''];
}

function toDateTime(value: unknown): DateTime | null {
  if (value instanceof DateTime) {
    return value.isValid ? value : null;
  }
  if (typeof value === 'string' && value) {
    const dt = DateTime.fromISO(value, { setZone: true });
    return dt.isValid ? dt : null;
  }
  return null;
}

function computeIsAllowed(
  values: unknown[],
  portalName: string | null,
): boolean[] {
  return values.map((value) =>
    reservationUtils.isAppointmentTimeAllowedForPortal(
      toDateTime(value),
      portalName,
    ),
  );
}

const appointmentDateTimeSubscription = fromCallback(
  ({
    sendBack,
    input,
  }: {
    sendBack: (event: { type: 'APPOINTMENT_DATETIMES_UPDATED' }) => void;
    input: AppointmentTimeAllowedForPortalInput;
  }) => {
    const emit = () => sendBack({ type: 'APPOINTMENT_DATETIMES_UPDATED' });
    const sub = input.appointmentDateTimeRef.subscribe(emit);
    emit();
    return () => sub.unsubscribe();
  },
);

export const appointmentTimeAllowedForPortalMachine = setup({
  types: {
    context: {} as AppointmentTimeAllowedForPortalContext,
    events: {} as AppointmentTimeAllowedForPortalEvent,
    input: {} as AppointmentTimeAllowedForPortalInput,
  },
  actors: {
    appointmentDateTimeSubscription,
  },
}).createMachine({
  id: 'appointmentTimeAllowedForPortal',
  context: ({ input }) => ({
    appointmentDateTimeRef: input.appointmentDateTimeRef,
    portalName: null,
    isAllowedByIndex: [true],
  }),
  invoke: {
    id: 'appointmentDateTimeSubscription',
    src: 'appointmentDateTimeSubscription',
    input: ({ context }) => ({
      appointmentDateTimeRef: context.appointmentDateTimeRef,
    }),
  },
  on: {
    APPOINTMENT_DATETIMES_UPDATED: {
      actions: assign(({ context }) => {
        const values = readAppointmentDateTimes(context.appointmentDateTimeRef);
        return {
          isAllowedByIndex: computeIsAllowed(values, context.portalName),
        };
      }),
    },
    SET_PORTAL_NAME: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'SET_PORTAL_NAME') return {};
        const values = readAppointmentDateTimes(context.appointmentDateTimeRef);
        return {
          portalName: event.portalName,
          isAllowedByIndex: computeIsAllowed(values, event.portalName),
        };
      }),
    },
    RESET: {
      actions: assign({
        isAllowedByIndex: () => [true],
      }),
    },
  },
});
